<template>
  <div class="pa-form-fields pa-proposer-form">
    <div class="form-row">
      <div class="input-group full">
        <label class="input-label">Student ID <span class="req">*</span></label>
        <v-text-field
          :value="form.studentId"
          outlined
          dense
          hide-details="auto"
          class="custom-input"
          placeholder="University registration no."
          :error-messages="errors.studentId"
          @input="update('studentId', $event)"
          @blur="touch('studentId')"
        />
      </div>
    </div>
    <div class="form-row">
      <div class="input-group full">
        <label class="input-label">University / institution <span class="req">*</span></label>
        <v-text-field
          :value="form.universityName"
          outlined
          dense
          hide-details="auto"
          class="custom-input"
          placeholder="e.g. University of Nairobi"
          :error-messages="errors.universityName"
          @input="update('universityName', $event)"
          @blur="touch('universityName')"
        />
      </div>
    </div>
    <div class="form-row">
      <div class="input-group">
        <label class="input-label">Telephone <span class="req">*</span></label>
        <v-text-field
          :value="form.phone"
          outlined
          dense
          hide-details="auto"
          class="custom-input"
          placeholder="07XXXXXXXX or 01XXXXXXXX"
          maxlength="10"
          :readonly="lockContactFields"
          :append-icon="lockContactFields ? 'mdi-lock-outline' : undefined"
          :error-messages="errors.phone"
          @input="onPhoneInput"
          @blur="touch('phone')"
        />
      </div>
      <div class="input-group">
        <label class="input-label">Email <span class="req">*</span></label>
        <v-text-field
          :value="form.email"
          outlined
          dense
          hide-details="auto"
          class="custom-input"
          placeholder="name@email.com"
          :readonly="lockContactFields"
          :append-icon="lockContactFields ? 'mdi-lock-outline' : undefined"
          :error-messages="errors.email"
          @input="update('email', $event)"
          @blur="touch('email')"
        />
      </div>
    </div>
    <div class="form-row">
      <div class="input-group full">
        <label class="input-label">Trade or occupation <span class="req">*</span></label>
        <v-text-field
          :value="form.occupation"
          outlined
          dense
          hide-details="auto"
          class="custom-input"
          :error-messages="errors.occupation"
          @input="update('occupation', $event)"
          @blur="touch('occupation')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { validateEmail, validatePhone } from '@/utils/paValidation';
import {
  AGENT_EMAIL_MESSAGE,
  AGENT_PHONE_MESSAGE,
  isAgentEmail,
  isAgentPhone,
} from '@/utils/agentSelfContact';

export default {
  name: 'PaProposerForm',
  props: {
    form: { type: Object, required: true },
    /** Customer journey: email/phone come from Cognito */
    lockContactFields: { type: Boolean, default: false },
    /** Agent journey: reject the signed-in agent's own email/phone as the customer's */
    rejectAgentContact: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
  },
  data() {
    return {
      touched: {},
      errors: {},
    };
  },
  methods: {
    update(field, value) {
      this.$emit('update:form', { ...this.form, [field]: value });
      if (this.touched[field]) this.validateField(field);
    },
    onPhoneInput(v) {
      const digits = String(v || '').replace(/\D/g, '').slice(0, 10);
      this.update('phone', digits);
    },
    touch(field) {
      this.$set(this.touched, field, true);
      this.validateField(field);
    },
    validateField(field) {
      const e = { ...this.errors };
      const f = this.form;
      switch (field) {
        case 'studentId':
          e.studentId = f.studentId?.trim() ? '' : 'Student ID is required';
          break;
        case 'universityName':
          e.universityName = f.universityName?.trim() ? '' : 'University is required';
          break;
        case 'phone': {
          const p = validatePhone(f.phone);
          e.phone = p.valid ? '' : p.message;
          if (p.valid && this.rejectAgentContact && isAgentPhone(f.phone, this.authenticatedUser)) {
            e.phone = AGENT_PHONE_MESSAGE;
          }
          if (p.valid && p.normalized !== f.phone) {
            this.$emit('update:form', { ...f, phone: p.normalized });
          }
          break;
        }
        case 'email': {
          const em = validateEmail(f.email);
          e.email = em.valid ? '' : em.message;
          if (em.valid && this.rejectAgentContact && isAgentEmail(f.email, this.authenticatedUser)) {
            e.email = AGENT_EMAIL_MESSAGE;
          }
          break;
        }
        case 'occupation':
          e.occupation = f.occupation?.trim() ? '' : 'Occupation is required';
          break;
        default:
          break;
      }
      this.errors = e;
    },
    validateAll() {
      const fields = ['studentId', 'universityName', 'phone', 'email', 'occupation'];
      fields.forEach((f) => {
        this.$set(this.touched, f, true);
        this.validateField(f);
      });
      return !Object.values(this.errors).some((msg) => msg);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/agent-pa-form.scss';
.pa-proposer-form { display: flex; flex-direction: column; gap: 8px; }
</style>
